import { isCatalogIndexSlug } from '@webbuilder/plans'

export type Page = {
  id: string
  name: string
  slug: string
  isHome: boolean
  isSlugCustom: boolean
  elements: unknown[]
  theme?: unknown
  pageStyles?: unknown

  // SEO
  seoTitle: string
  seoDescription: string
  canonicalUrl: string

  // Open Graph
  ogTitle: string
  ogDescription: string
  ogImage: string | null

  // Access
  accessMode: 'public' | 'password'
  password: string

  // Advanced
  indexable: boolean
  includeInSitemap: boolean
  redirect301: string | null
  customHeadCode: string

  // CMS — present only on dynamic pages (collection/item templates). Preserved so
  // dashboard saves don't strip it, and so these pages can be hidden from the list.
  cmsDynamic: { kind?: 'collection' | 'item'; collection?: string; slugField?: string; previewItemId?: string } | null

  // Metadata
  thumbnailUrl: string | null
  createdAt: string
  updatedAt: string
}

export function normalizePage(raw: Record<string, unknown>): Page {
  const now = new Date().toISOString()
  const isHome = (raw.isHome as boolean) ?? false
  const name = (raw.name as string) ?? 'Page'
  const defaultSlug = isHome ? '/' : generateSlug(name) || '/pagina'
  return {
    id: (raw.id as string) ?? crypto.randomUUID(),
    name,
    slug: (raw.slug as string) ?? defaultSlug,
    isHome,
    isSlugCustom: (raw.isSlugCustom as boolean) ?? false,
    elements: (raw.elements as unknown[]) ?? [],
    ...(raw.theme ? { theme: raw.theme } : {}),
    pageStyles: raw.pageStyles,
    seoTitle: (raw.seoTitle as string) ?? '',
    seoDescription: (raw.seoDescription as string) ?? '',
    canonicalUrl: (raw.canonicalUrl as string) ?? '',
    ogTitle: (raw.ogTitle as string) ?? '',
    ogDescription: (raw.ogDescription as string) ?? '',
    ogImage: (raw.ogImage as string | null) ?? null,
    accessMode: (raw.accessMode as 'public' | 'password') === 'password' ? 'password' : 'public',
    password: (raw.password as string) ?? '',
    indexable: (raw.indexable as boolean) ?? true,
    includeInSitemap: (raw.includeInSitemap as boolean) ?? true,
    redirect301: (raw.redirect301 as string | null) ?? null,
    customHeadCode: (raw.customHeadCode as string) ?? '',
    cmsDynamic: raw.cmsDynamic && typeof raw.cmsDynamic === 'object' ? (raw.cmsDynamic as Page['cmsDynamic']) : null,
    thumbnailUrl: (raw.thumbnailUrl as string | null) ?? null,
    createdAt: (raw.createdAt as string) ?? now,
    updatedAt: (raw.updatedAt as string) ?? now,
  }
}

export function generateSlug(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  return '/' + slug
}

export function ensureUniqueSlug(
  slug: string,
  pages: { id: string; slug: string }[],
  excludeId?: string
): string {
  const taken = new Set(pages.filter((p) => p.id !== excludeId).map((p) => p.slug))
  if (!taken.has(slug)) return slug
  let i = 2
  while (taken.has(`${slug}-${i}`)) i++
  return `${slug}-${i}`
}

/** Pages that belong in the dashboard's regular Pages surfaces.
 * CMS collection/item templates live under Contenido, while editor template
 * documents live under the template library. Keep both out of page counts and
 * lists without removing them from the project data used for saves. */
export function dashboardSitePages(pages: Page[]): Page[] {
  const dynamicPlaceholder = /\[[^\]]*\]/
  const normalizeSlug = (slug: string) => (slug || '').replace(/\/+$/, '')
  const collectionParents = new Set(
    pages
      .filter((page) => dynamicPlaceholder.test(page.slug ?? ''))
      .map((page) => normalizeSlug((page.slug ?? '').replace(/\/?\[[^\]]*\].*$/, '')))
      .filter(Boolean)
  )

  return pages.filter((page) => {
    const slug = page.slug ?? ''
    const isCmsPage = Boolean(page.cmsDynamic)
      || dynamicPlaceholder.test(slug)
      || (!page.isHome && collectionParents.has(normalizeSlug(slug)))
    const isTemplateDocument = page.id.startsWith('template:')
    /* Las del catálogo (`/services`…) existen siempre y no ocupan una página del plan. */
    return !isCmsPage && !isTemplateDocument && !isCatalogIndexSlug(slug)
  })
}
